// src/pages/accessibility-statement/AccessibilityStatementPage.tsx

import React from 'react';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';
import { Link } from 'react-router-dom';

const AccessibilityStatementPage: React.FC = () => {
  const analytics = useAnalyticsWithConsent();

  analytics.trackPixelPageView();

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-4xl font-bold mb-6">Accessibility Statement</h1>

      <section className="mb-8">
        <p className="mb-4">
          RentSmart.ie is committed to ensuring digital accessibility for people
          with disabilities. We are continually improving the user experience
          for everyone and applying the relevant accessibility standards to
          ensure we provide equal access to all of our users.
        </p>
        <p className="mb-4">
          <strong>Last updated:</strong>{' '}
          {new Date().toLocaleDateString('en-IE', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">1. Conformance Status</h2>
        <p className="mb-4">
          RentSmart.ie strives to conform to the{' '}
          <strong>
            Web Content Accessibility Guidelines (WCAG) 2.1 Level AA
          </strong>
          , as required by the{' '}
          <strong>European Accessibility Act (EAA) and EN 301 549</strong>{' '}
          harmonised standard. These guidelines explain how to make web content
          more accessible for people with disabilities and improve usability for
          all users.
        </p>
        <p className="mb-4">
          WCAG 2.1 Level AA conformance means our website meets all Level A and
          Level AA success criteria, or a conforming alternate version is
          provided.
        </p>
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
          <p className="text-sm">
            <strong>Note:</strong> We are committed to ongoing testing and
            improvement to achieve and maintain full WCAG 2.1 AA conformance
            across all features and pages.
          </p>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          2. Accessibility Features
        </h2>
        <p className="mb-4">
          Our website includes the following accessibility features:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>
            <strong>Keyboard Navigation:</strong> All interactive elements can
            be accessed and operated using a keyboard alone, including Tab,
            Shift+Tab, Enter, Space, and Arrow keys
          </li>
          <li>
            <strong>Screen Reader Compatibility:</strong> Semantic HTML
            structure, ARIA labels, and descriptive text alternatives for images
            ensure compatibility with screen readers such as JAWS, NVDA, and
            VoiceOver
          </li>
          <li>
            <strong>Text Alternatives:</strong> All non-text content (images,
            icons, charts) includes appropriate alternative text descriptions
          </li>
          <li>
            <strong>Color Contrast:</strong> Text and interactive elements meet
            WCAG 2.1 Level AA contrast requirements (minimum 4.5:1 for normal
            text, 3:1 for large text)
          </li>
          <li>
            <strong>Resizable Text:</strong> Content remains readable and
            functional when text is resized up to 200% without loss of
            functionality
          </li>
          <li>
            <strong>Focus Indicators:</strong> All interactive elements display
            clear, visible focus indicators when navigated via keyboard
          </li>
          <li>
            <strong>Responsive Design:</strong> Content reflows and adapts to
            different screen sizes and orientations without requiring horizontal
            scrolling at standard zoom levels
          </li>
          <li>
            <strong>Form Labels and Instructions:</strong> All form inputs
            include clear, descriptive labels and error messages are announced
            to assistive technologies
          </li>
          <li>
            <strong>Skip Navigation Links:</strong> Skip-to-content links allow
            keyboard users to bypass repetitive navigation elements
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          3. Known Accessibility Limitations
        </h2>
        <p className="mb-4">
          Despite our best efforts to ensure accessibility, some limitations may
          exist. Below are the known issues we are actively working to address:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-3">
          <li>
            <strong>Third-Party Virtual Tours:</strong> Virtual tour content
            embedded from third-party providers (such as Matterport or similar
            services) may not be fully accessible. We do not have direct control
            over these external services, but we encourage landlords to provide
            alternative text descriptions and contact information for inquiries.
          </li>
          <li>
            <strong>User-Generated Content:</strong> Property listings, photos,
            and descriptions are created by landlords and property owners. While
            we provide guidance on accessibility best practices, we cannot
            guarantee that all user-generated content meets accessibility
            standards. We encourage users to report inaccessible content through
            our contact form below.
          </li>
          <li>
            <strong>PDF Documents:</strong> Some downloadable resources, guides,
            or documents may not be fully accessible. We are working to ensure
            all PDF files are tagged, include alternative text for images, and
            are compatible with assistive technologies.
          </li>
          <li>
            <strong>Complex Interactive Maps:</strong> Interactive property
            location maps powered by third-party services
            (Leaflet/OpenStreetMap) may have limited keyboard navigation and
            screen reader support. We provide text-based location information as
            an alternative.
          </li>
          <li>
            <strong>Legacy Features:</strong> Some older components and pages
            may not fully conform to WCAG 2.1 AA standards. We are
            systematically reviewing and updating these areas to meet current
            accessibility requirements.
          </li>
        </ul>
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
          <p className="text-sm">
            <strong>Important:</strong> If you encounter any accessibility
            barriers not listed here, please report them using the contact
            information provided in Section 6 below.
          </p>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          4. Assistive Technologies and Browser Compatibility
        </h2>
        <p className="mb-4">
          RentSmart.ie is designed to be compatible with the following assistive
          technologies and browsers:
        </p>
        <h3 className="text-xl font-semibold mb-3">Assistive Technologies</h3>
        <ul className="list-disc pl-6 mb-4">
          <li>JAWS (Job Access With Speech) - latest version</li>
          <li>NVDA (NonVisual Desktop Access) - latest version</li>
          <li>VoiceOver (macOS/iOS) - latest version</li>
          <li>TalkBack (Android) - latest version</li>
          <li>ZoomText and other screen magnification software</li>
          <li>Dragon NaturallySpeaking and other voice recognition software</li>
        </ul>
        <h3 className="text-xl font-semibold mb-3">Supported Browsers</h3>
        <ul className="list-disc pl-6 mb-4">
          <li>Google Chrome (latest version)</li>
          <li>Mozilla Firefox (latest version)</li>
          <li>Apple Safari (latest version)</li>
          <li>Microsoft Edge (latest version)</li>
        </ul>
        <p className="mb-4">
          We recommend using the latest version of your chosen browser for the
          best accessibility experience. Older browser versions may not support
          all accessibility features.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          5. Ongoing Accessibility Efforts
        </h2>
        <p className="mb-4">
          Accessibility is an ongoing commitment. We are continuously working to
          improve the accessibility of RentSmart.ie through the following
          efforts:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>
            <strong>Regular Audits:</strong> We conduct periodic accessibility
            audits using automated tools (e.g., jest-axe, Lighthouse) and manual
            testing with assistive technologies
          </li>
          <li>
            <strong>Accessibility-First Design:</strong> New features and
            components are designed with accessibility in mind from the start,
            following WCAG 2.1 AA principles
          </li>
          <li>
            <strong>Continuous Monitoring:</strong> We monitor and respond to
            accessibility issues reported by users and identified through
            automated testing
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          6. Feedback and Contact Information
        </h2>
        <p className="mb-4">
          We welcome your feedback on the accessibility of RentSmart.ie. If you
          encounter any accessibility barriers, have suggestions for
          improvement, or require assistance accessing any content or features,
          please contact us using one of the methods below:
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-4">
          <h3 className="text-xl font-semibold mb-3">Contact Methods</h3>
          <ul className="space-y-3">
            <li>
              <strong>General Support Email:</strong>{' '}
              <a
                href="mailto:support@rentsmart.ie"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                support@rentsmart.ie
              </a>
            </li>
            <li>
              <strong>Contact Form:</strong>{' '}
              <Link
                to="/contact"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Submit via our Contact Page
              </Link>
            </li>
          </ul>
        </div>
        <p className="mb-4">
          When reporting an accessibility issue, please include the following
          information to help us address the problem quickly:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>A description of the accessibility barrier you encountered</li>
          <li>The URL of the page where the issue occurred</li>
          <li>
            The browser and assistive technology you were using (if applicable)
          </li>
          <li>Steps to reproduce the issue</li>
          <li>Your contact information for follow-up (optional)</li>
        </ul>
        <p className="mb-4">
          <strong>Response Time:</strong> We aim to respond to all accessibility
          inquiries within 3-5 business days. Complex issues may require
          additional time to investigate and resolve, but we will keep you
          informed of our progress.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          8. Technical Specifications
        </h2>
        <p className="mb-4">
          RentSmart.ie accessibility relies on the following technologies:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>HTML5</li>
          <li>CSS3</li>
          <li>JavaScript (ES6+)</li>
          <li>React 18</li>
          <li>TypeScript</li>
          <li>ARIA (Accessible Rich Internet Applications)</li>
          <li>WAI-ARIA 1.2</li>
        </ul>
        <p className="mb-4">
          These technologies are used in accordance with W3C specifications and
          accessibility best practices to ensure compatibility with assistive
          technologies.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          9. Related Policies and Guidelines
        </h2>
        <p className="mb-4">
          For more information about how we protect your data and ensure a safe
          user experience, please review our related policies:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <Link
              to="/privacy-policy"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              Privacy Policy
            </Link>{' '}
            - How we collect, use, and protect your personal information
          </li>
          <li>
            <Link
              to="/terms-of-service"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              Terms of Service
            </Link>{' '}
            - Terms and conditions for using RentSmart.ie
          </li>
          <li>
            <Link
              to="/cookie-policy"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              Cookie Policy
            </Link>{' '}
            - How we use cookies and tracking technologies
          </li>
          <li>
            <Link
              to="/community-guidelines"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              Community Guidelines
            </Link>{' '}
            - Standards for respectful and inclusive behavior on our platform
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          10. Accessibility Resources
        </h2>
        <p className="mb-4">
          For more information about web accessibility and assistive
          technologies, we recommend the following resources:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <a
              href="https://www.w3.org/WAI/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              W3C Web Accessibility Initiative (WAI)
            </a>
          </li>
          <li>
            <a
              href="https://www.w3.org/WAI/WCAG21/quickref/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              WCAG 2.1 Quick Reference Guide
            </a>
          </li>
          <li>
            <a
              href="https://www.etsi.org/deliver/etsi_en/301500_301599/301549/03.02.01_60/en_301549v030201p.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              EN 301 549 Standard (PDF)
            </a>
          </li>
          <li>
            <a
              href="https://www.includability.gov.ie/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              National Disability Authority Ireland - Web Accessibility
            </a>
          </li>
          <li>
            <a
              href="https://webaim.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              WebAIM - Web Accessibility In Mind
            </a>
          </li>
        </ul>
      </section>

      <section className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h2 className="text-2xl font-semibold mb-4">
          Our Commitment to Accessibility
        </h2>
        <p className="mb-4">
          At RentSmart.ie, we believe that everyone should have equal access to
          housing information and opportunities. Accessibility is not just a
          legal requirement - it is a fundamental part of our mission to create
          an inclusive platform that serves all members of the community.
        </p>
        <p className="mb-4">
          We are committed to maintaining and improving the accessibility of our
          website over time. Thank you for choosing RentSmart.ie, and please
          don't hesitate to reach out if you need any assistance.
        </p>
        <p className="text-sm italic">
          This Accessibility Statement is reviewed and updated regularly to
          reflect our ongoing commitment to digital accessibility and compliance
          with the European Accessibility Act (EAA) and WCAG 2.1 Level AA
          standards.
        </p>
      </section>
    </div>
  );
};

export default AccessibilityStatementPage;
